import groupModule from "./group.store";
import tasksModule from "./tasks.store";
import { get, cache, watch, watch_document, fetch, fetch_document } from "./helpers/actions.store";
import { set_fetch_timestamp, set_document_listener } from "./helpers/mutations.store";
import { is_watching, document_listener, stored } from "./helpers/getters.store";
import { cleanupObj, pathTail, pathHead } from "../helpers.js";
import { storage, firestore } from "@/firebase";

import {
  ALL_GROUPS,
  DEFAULT_GROUP,
  GROUP_STORED,
  GROUP,
  IS_WATCHING,
  STORED,
  DOCUMENT_LISTENER,
  ID,
  REF,
  CACHED,
  IN_GROUP,
  AVATAR_STORAGE_REF,
  USER_ID,
  LAST_FETCHED,
  NEVER_FETCHED,
  FCM_TOKEN,
  SELECTED_CONTEXT,
  SELECTED_GROUP,
} from "./helpers/getter.type";

import {
  SELECT_CONTEXT, SELECT_GROUP, SET_FCM_TOKEN,
} from "./helpers/mutation.type";

import {
  DELETE_AVATAR_IMAGE,
  FETCH,
  GET_GROUP,
  GET_USER_DEFAULT_CONTEXT,
  UPDATE,
  UPDATE_DATABASE_USER_DOC,
  UPLOAD_AVATAR_IMAGE,
  UPDATE_AVATAR_PHOTO,
  GET_FROM_MODULES,
  REGISTER_USER_NOTIFICATION_TOKEN,
  GET_SELECTED,
  WATCH,
  SELECT_GET_DEFAULTS,
  MAYBE_WATCH,
} from "./helpers/action.type";


export const getters = {
  [CACHED]: (state,_, rootState) => ({
    ...rootState.user,
    displayName: state.name,
    photoURL: state.photoURL,
    email: state.email,
    groups: state.groups
  }),
  [REF]: (_s,_g,_rs, rootGetters) => firestore.doc(`/users/${rootGetters.user_id}`),
  [ID]: (_s, getters) => getters.ref.id,
  [ALL_GROUPS]: state => state.groups,
  [GROUP]: state => group_id => state.groups[group_id],
  [GROUP_STORED]: state => group_id => group_id in state,
  [DEFAULT_GROUP]: state => {
    // TODO: Remove this getter.
    console.warn("This is only for the MVP, will be deprecated in the future.");
    if (state.groups.length !== 1) {
      console.error("Expected exactly one group to be available.");
      return null;
    } else {
      return state.groups[0];
    }
  },
  [IN_GROUP]: state => group_id => state.groups.map(group => group.id).includes(group_id),
  [AVATAR_STORAGE_REF]: (_s,_g,_rs, rootGetters) =>
    name => storage.ref().child(`${rootGetters.user_id}/avatar/${+new Date()}-${name}`),
  [IS_WATCHING]: is_watching,
  [DOCUMENT_LISTENER]: document_listener,
  [STORED]: stored,
  [USER_ID]: state => state.uid,
  [LAST_FETCHED]: state => state.lastFetched,
  [FCM_TOKEN]: state => state.fcm_token ?? null,
  [SELECTED_GROUP]: state => state.selected_group,
  [SELECTED_CONTEXT]: state => state.selected_context,
};

export const mutations = {
  append_group(state, group_ref) {
    this.registerModule(["user", group_ref.id], groupModule(group_ref.id, group_ref));
  },
  set_document(state, payload) {
    const data = payload.data();
    state.uid = payload.id;
    state.groups = data.groups;
    state.name = data.name;
    state.email = data.email;
    state.photoURL = data.photoURL ?? null;
    state.status = "fetched";
    state.fcm_token = payload.fcm_token ?? null;
  },
  set_fetch_timestamp,
  set_document_listener,
  [SELECT_CONTEXT]: (state, new_context) => state.selected_context = new_context,
  [SELECT_GROUP]: (state, new_group) => state.selected_group = new_group,
  [SET_FCM_TOKEN]: (state, token) => state.fcm_token = token,
};

export const actions = {
  [UPDATE]: async ({ dispatch, rootGetters }, { name, email, password, avatarImage }) => {
    const promises = [];
    email && promises.push(rootGetters.currentUser.updateEmail(email));
    password && promises.push(rootGetters.currentUser.updatePassword(password));
    const photoURL = avatarImage && await dispatch("update_avatar_photo", { avatarImage });
    if (email || name || avatarImage)
      promises.push(dispatch("update_database_user_doc", { name, email, photoURL }))
    
    await Promise.all(promises);
  },
  [DELETE_AVATAR_IMAGE]: async ({ getters }) => {
    if (getters.cached.photoURL)
      await storage.refFromURL(getters.cached.photoURL).delete();
  },
  [UPDATE_AVATAR_PHOTO]: async ({ dispatch }, { avatarImage }) => {
    const resolved = await Promise.all([
      dispatch("delete_avatar_image"),
      dispatch("upload_avatar_image", { image: avatarImage })
    ]);
    return resolved[1];
  },
  [UPLOAD_AVATAR_IMAGE]: async ({ getters }, { image }) => {
    const metadata = { contentType: image.type };
    const snapshot = await getters.avatar_storage_ref(image.name).put(image, metadata);
    return snapshot.ref.getDownloadURL();
  },
  [UPDATE_DATABASE_USER_DOC]: async ({ getters }, { name, email, photoURL }) => {
    const updateObj = { name, email, photoURL };
    await getters.ref.update(cleanupObj(updateObj));
  },
  get,
  cache,
  watch,
  watch_document,
  fetch,
  fetch_document,
  [GET_FROM_MODULES]: async ({ dispatch }, payload) => {
    switch (pathHead(payload.path)) {
      // If the group is the first in the path, redirect the get action to get_group.
      case "groups":
      case "group":
        return await dispatch("get_group", { ...payload, path: pathTail(payload.path) });
      
      default:
        return getters.cached[pathHead(payload.path)];
    }
  },
  [GET_GROUP]: async ({ getters, commit, dispatch }, payload) => {
    const group_id = pathHead(payload.path);
    if (!group_id) throw new Error("Tried to get group but no group id was in the path.");
    
    if (getters.in_group(group_id)) {
      if (!getters.group_stored(group_id)) {
        const ref = firestore.doc(`/groups/${group_id}`);
        commit("append_group", ref);
      }
      return await dispatch(`${group_id}/get`, { ...payload, path: pathTail(payload.path) });
    } else {
      throw new Error(`Unable to get a group that the you are not a part of: ${group_id}`);
    }
  },
  async get_default_group({ dispatch, rootGetters }, options) {
    if (!rootGetters.loggedIn) {
      throw new Error("Unable to get default while user is not logged in.");
    }
    return await dispatch("get_group", {
      path: rootGetters.user_id,
      options,
    });
  },
  [GET_USER_DEFAULT_CONTEXT]: async ({ getters, dispatch }, options) => {
    if (getters[LAST_FETCHED] === NEVER_FETCHED)
      await dispatch(FETCH);

    return await dispatch(GET_GROUP, {
      path: `${getters[USER_ID]}/context/default`,
      options,
    });
  },
  [REGISTER_USER_NOTIFICATION_TOKEN]: async ({ getters, dispatch }, token) => {
    if (getters[LAST_FETCHED] == NEVER_FETCHED)
      await dispatch(FETCH)

    const registered_token = getters[FCM_TOKEN];
    if (!registered_token || registered_token !== token) {
      const ref = getters[REF];
      await ref.update({ fcm_token: token });
    }
  },

  /**
   * Garantees that, after this call, the user data will be in the store.
   * @param {*} action_params
   * @param {*} options 
   */
  [MAYBE_WATCH]: async ({ getters, dispatch }, options) => {
    if (!getters[IS_WATCHING] && options?.watch !== false && options?.forceWatch !== false) {
      await dispatch(WATCH);
    } else if (getters[LAST_FETCHED] == NEVER_FETCHED) {
      await dispatch(FETCH);
    }
  },

  /**
   * Assumes the watch option is set to true.
   * @param {*} action_param
   * @param {*} options 
   */
  [GET_SELECTED]: async ({ getters, dispatch }, options) => {
    if (!getters[SELECTED_GROUP] || !getters[SELECTED_CONTEXT]) {
      console.error("A group and context must be selected.");
    }

    dispatch(MAYBE_WATCH, options);

    return await dispatch(GET_GROUP, {
      path: `${getters[SELECTED_GROUP]}/context/${getters[SELECTED_CONTEXT]}`,
      options: {
        ...options,
        watch: options?.watch ?? true,
        forceWatch: options?.forceWatch ?? false,
      },
    })
  },

  [SELECT_GET_DEFAULTS]: async ({ getters, commit, dispatch }, options) => {
    // Needs to be done first, so we know the user id.
    await dispatch(MAYBE_WATCH, options);
    commit(SELECT_GROUP, getters[USER_ID]);
    commit(SELECT_CONTEXT, "default");
    console.important(`group: ${getters[SELECTED_GROUP]}, context: ${getters[SELECTED_CONTEXT]}`);
    return await dispatch(GET_SELECTED, options);
  },
};

export const state = () => ({
  displayName: null,
  fcm_token: null,
  photoURL: null,
  email: "",
  groups: [],
  uid: "",
  selected_context: "",
  selected_group: "",
  lastFetched: NEVER_FETCHED,
  document_listener: null,

  // Only used for debbugging. If true, will always send email instead of notifications.
  force_email: false,
});

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: {
    tasks: tasksModule("tasks")
  }
}
